<script setup lang="ts">
/**
 * LumiAssistantPanel — the suite-wide Lumi assistant, one shared implementation
 * for every internal UI (ADR 0027, ADR 0014 amendment). It wraps the
 * presentational LumiShell (float/dock/minimize) and fills its body/footer with
 * the conversation, run approvals and composer.
 *
 * STATEFUL but HEADLESS: all app coupling — network, navigation, page context,
 * confirm, i18n text — is injected via the `transport` prop and `labels`, so the
 * library still imports nothing but Vue and stays token-styled + alias-only. The
 * business logic lives in the injected-transport composable `useLumiAssistant`.
 *
 * Continuity across module switches (which are full page loads) is "remount fast
 * + rehydrate": mode, expanded/minimized state and the active conversationId all
 * persist per tenant+user, so Lumi reappears exactly where the user left it within
 * the new page's own load. Mode and conversationId live in localStorage; the
 * expanded/minimized state lives in sessionStorage (same tab, same origin), so a
 * panel left open yesterday does not pop up again at the next login (issue 718).
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import LumiShell from './LumiShell.vue';
import { lumiDockLauncher } from './lumiDockLauncher';
import LumiRunCard from './LumiRunCard.vue';
import LumiMarkdown from '../display/LumiMarkdown.vue';
import Spinner from '../feedback/Spinner.vue';
import type { LumiModuleKey } from '../modules';
import {
  ASSISTANT_UPLOAD_ACCEPT,
  useLumiAssistant,
  type AssistantAttachmentDraft,
  type AssistantMessageView,
  type AssistantRunView,
  type AssistantTransport,
  type AssistantUploadView,
} from './useLumiAssistant';
import LumiIcon from '../icons/LumiIcon.vue';

export interface AssistantLabels {
  title: string;
  you: string;
  ai: string;
  actions: string;
  /** The run card's `needs_approval` state pill — the warning tone (spec §6, issue 534). */
  needsApproval: string;
  approve: string;
  cancel: string;
  // The run card's other five status words + affordances (issue 534) — see
  // LumiRunCard.vue's `statusLabel` for exactly which run states use which.
  runStatusSucceeded: string;
  runStatusFailed: string;
  runStatusCancelled: string;
  runStatusRunning: string;
  runStatusRefused: string;
  runStatusExpired: string;
  runDetailsShow: string;
  runDetailsHide: string;
  runOpen: string;
  /** Shown once per turn when it holds more than one `needs_approval` card. */
  runApproveAll: string;
  /** The pill that appears when Lumi answered while the person had scrolled
   *  up in the thread; clicking it jumps to the newest message (issue 558). */
  newMessage: string;
  inputPlaceholder: string;
  send: string;
  sending: string;
  loading: string;
  newSessionLabel: string;
  newSessionTitle: string;
  newSessionBody: string;
  minimizeLabel: string;
  dockLabel: string;
  floatLabel: string;
  resizeLabel: string;
  launcherLabel: string;
  sendError: string;
  actionError: string;
  // The approval card's structured, editable draft (#275, D3/D11/D15).
  draftLoading: string;
  draftCandidate: string;
  draftApplication: string;
  draftJob: string;
  /** The loaded draft's own summary prose (F7b, review pass) — shown next to
   *  the structured fields, e.g. a job posting's `read_draft` summary. */
  draftSummary: string;
  draftSubject: string;
  draftBody: string;
  draftEdited: string;
  draftErrorNotAvailable: string;
  draftErrorNotFound: string;
  draftErrorNoMessage: string;
  draftErrorPermission: string;
  draftErrorGeneric: string;
  approveErrorPermission: string;
  approveErrorGeneric: string;
  // "That answer was wrong", in one click (issue 296, ADR 0071 D5, PDR 0023 rule 4).
  feedbackUp: string;
  feedbackDown: string;
  feedbackThanks: string;
  feedbackFailed: string;
  feedbackReasonTitle: string;
  feedbackReasonWrongResult: string;
  feedbackReasonDidNothing: string;
  feedbackReasonMisunderstood: string;
  feedbackReasonTooSlow: string;
  feedbackReasonOther: string;
  feedbackTextPlaceholder: string;
  feedbackSubmit: string;
  feedbackDismiss: string;
  /**
   * Lumi's opening line, at the top of a thread nobody has spoken in yet (#419,
   * #678). The panel opens itself to say it only to a person meeting Lumi for the
   * first time (the server minted their conversation just now).
   *
   * NOT persisted as a message: it is UI copy in nine languages, and a conversation
   * that starts with a stored assistant turn nobody asked for would carry it into
   * every later read of that thread. What IS durable is whether the person has met
   * Lumi, which the server answers by having just minted their conversation.
   *
   * An EMPTY string switches the whole behaviour off — no greeting and no
   * self-opening — which is how a host suppresses it (a workspace in demo mode).
   */
  greeting: string;
  /**
   * The short line at the top of an empty thread that is NOT a first contact
   * (issue 718): a cleared session, a quiet day. The long `greeting` is Lumi's
   * introduction and is said once; this is just an invitation to ask.
   */
  emptyThread: string;
  // Files dropped into Lumi (ADR 0106): the paperclip, the drop zone, a chip's
  // remove button and the reasons a file was not taken.
  attach: string;
  /** The camera button on a touch screen: a photo straight into the message. */
  takePhoto: string;
  dropHere: string;
  attachRemove: string;
  attachUploading: string;
  attachErrorType: string;
  attachErrorSize: string;
  attachErrorTooMany: string;
  attachErrorScan: string;
  attachErrorGeneric: string;
}

const DEFAULT_LABELS: AssistantLabels = {
  title: 'lumi',
  you: 'Du',
  ai: 'lumi',
  actions: 'Aktionen',
  needsApproval: 'Bestätigung nötig',
  approve: 'Freigeben',
  cancel: 'Abbrechen',
  runStatusSucceeded: 'Ausgeführt',
  runStatusFailed: 'Nicht ausgeführt',
  runStatusCancelled: 'Verworfen',
  runStatusRunning: 'Läuft …',
  runStatusRefused: 'Abgelehnt',
  runStatusExpired: 'Abgelaufen',
  runDetailsShow: 'Details anzeigen',
  runDetailsHide: 'Details ausblenden',
  runOpen: 'Öffnen',
  runApproveAll: 'Alle bestätigen',
  newMessage: 'Neue Nachricht',
  inputPlaceholder: 'Frag lumi …',
  send: 'Senden',
  sending: 'Senden …',
  loading: 'Lädt …',
  newSessionLabel: 'Neue Unterhaltung',
  newSessionTitle: 'Neue Unterhaltung starten?',
  newSessionBody: 'Die aktuelle Unterhaltung mit lumi wird endgültig gelöscht.',
  minimizeLabel: 'Minimieren',
  dockLabel: 'Andocken',
  floatLabel: 'Schweben lassen',
  resizeLabel: 'Breite anpassen',
  launcherLabel: 'lumi',
  sendError: 'Fehler beim Senden.',
  actionError: 'Die Aktion konnte nicht ausgeführt werden.',
  draftLoading: 'Details werden geladen …',
  draftCandidate: 'Kandidat:in',
  draftApplication: 'Bewerbungsnummer',
  draftJob: 'Stelle',
  draftSummary: 'Zusammenfassung',
  draftSubject: 'Betreff',
  draftBody: 'Text',
  draftEdited: 'Bearbeitet',
  draftErrorNotAvailable: 'Für diese Aktion gibt es keinen Entwurf zum Ansehen.',
  draftErrorNotFound: 'Der Entwurf wurde nicht gefunden.',
  draftErrorNoMessage: 'Dieser Entwurf enthält keine Nachricht mehr, die Du freigeben kannst.',
  draftErrorPermission: 'Du darfst diesen Entwurf nicht sehen.',
  draftErrorGeneric: 'Der Entwurf konnte nicht geladen werden.',
  approveErrorPermission: 'Du darfst diese Aktion nicht freigeben.',
  approveErrorGeneric: 'Die Freigabe ist fehlgeschlagen; es wurde nichts gesendet.',
  feedbackUp: 'War hilfreich',
  feedbackDown: 'War nicht hilfreich',
  feedbackThanks: 'Danke für die Rückmeldung.',
  feedbackFailed: 'Die Rückmeldung konnte nicht gespeichert werden.',
  feedbackReasonTitle: 'Was war nicht richtig?',
  feedbackReasonWrongResult: 'Falsches Ergebnis',
  feedbackReasonDidNothing: 'Es ist nichts passiert',
  feedbackReasonMisunderstood: 'Falsch verstanden',
  feedbackReasonTooSlow: 'Zu langsam',
  feedbackReasonOther: 'Anderes',
  feedbackTextPlaceholder: 'Optional: was hätte passieren sollen?',
  feedbackSubmit: 'Absenden',
  feedbackDismiss: 'Verwerfen',
  greeting:
    'Willkommen! Ich bin lumi. Ich helfe dir gern weiter und beantworte deine Fragen, während du dich zurechtfindest — und ich unterstütze dich auch bei deiner täglichen Arbeit.',
  emptyThread: 'Wie kann ich dir helfen?',
  attach: 'Datei anhängen',
  takePhoto: 'Foto aufnehmen',
  dropHere: 'Dateien hier ablegen',
  attachRemove: 'Entfernen',
  attachUploading: 'Wird hochgeladen …',
  attachErrorType: 'Nur PDF, DOCX, PNG und JPEG',
  attachErrorSize: 'Größer als 10 MB',
  attachErrorTooMany: 'Höchstens 5 Dateien pro Nachricht',
  attachErrorScan: 'Vom Virenscan abgelehnt',
  attachErrorGeneric: 'Konnte nicht hochgeladen werden',
};

// The five reason codes and the label each renders under (ADR 0071 D5). The
// CODES are what travel — to `assistant-api`, and from there as the only part of
// the person's feedback that ever reaches the operator's cross-tenant queue.
const FEEDBACK_REASONS = [
  { code: 'wrong_result', label: 'feedbackReasonWrongResult' },
  { code: 'did_nothing', label: 'feedbackReasonDidNothing' },
  { code: 'misunderstood', label: 'feedbackReasonMisunderstood' },
  { code: 'too_slow', label: 'feedbackReasonTooSlow' },
  { code: 'other', label: 'feedbackReasonOther' },
] as const;

const props = defineProps<{
  /** The app adapter: network, navigation, page context, confirm, link resolve. */
  transport: AssistantTransport;
  /** Suite/module accent for the shell. */
  module?: LumiModuleKey;
  /** localStorage key for float/dock mode (per tenant+user). */
  modeStorageKey?: string;
  /** sessionStorage key for expanded/minimized (per tenant+user) — survives module switches, not a new session. */
  stateStorageKey?: string;
  /** localStorage key for the active conversationId (per tenant+user). */
  conversationStorageKey?: string;
  /**
   * Open in the dock on the first page of a browser session (a workspace in demo
   * mode): Lumi is part of what a demo shows. Once the person minimizes it, the
   * stored state wins for the rest of the session, like any other.
   */
  openOnArrival?: boolean;
  pollMs?: number;
  labels?: Partial<AssistantLabels>;
}>();

// Merge defaults + overrides once per labels change, not ~13× per render (CL3).
const L = computed<AssistantLabels>(() => ({ ...DEFAULT_LABELS, ...(props.labels || {}) }));

const shellRef = ref<InstanceType<typeof LumiShell> | null>(null);
const messagesEl = ref<HTMLElement | null>(null);
const expanded = ref(false);

const lumi = useLumiAssistant({
  transport: props.transport,
  expanded,
  conversationStorageKey: () => props.conversationStorageKey,
  pollMs: props.pollMs,
  labels: {
    title: L.value.title,
    newSessionTitle: L.value.newSessionTitle,
    newSessionBody: L.value.newSessionBody,
    sendError: L.value.sendError,
    actionError: L.value.actionError,
  },
});

// Lumi is busy — a turn awaiting its answer, or a run queued or running: the
// sidebar button's rays pulse, open or closed (lumiDockLauncher `working`).
watch(
  () => lumi.sending.value || lumi.runs.value.some((r) => r.status === 'queued' || r.status === 'running'),
  (busy) => { lumiDockLauncher.working = busy; },
  { immediate: true },
);

// CHAT-STYLE SCROLLING (issue 558). The thread follows new content only while
// the person is at its bottom: `pinned` is re-read on every scroll of the
// thread, so a person who scrolled up to read something keeps their place when
// Lumi answers, and gets a "new message" pill to jump down instead. Sending
// always jumps to the bottom — your own sentence is where you are looking.
const PINNED_SLACK_PX = 48;
const pinned = ref(true);
const newBelow = ref(false);

function onBodyScroll() {
  const el = messagesEl.value;
  if (!el) return;
  pinned.value = el.scrollHeight - el.scrollTop - el.clientHeight <= PINNED_SLACK_PX;
  if (pinned.value) newBelow.value = false;
}

function scrollToBottom() {
  pinned.value = true;
  newBelow.value = false;
  nextTick(() => {
    const el = messagesEl.value;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  });
}

// Expanded/minimized is remembered for THIS browser session only (issue 718): a
// module switch is a full page load in the same tab and keeps it, but signing in
// again later starts minimized — the panel opens unasked on first contact alone.
function readStoredState(): 'expanded' | 'minimized' | null {
  if (!props.stateStorageKey || typeof sessionStorage === 'undefined') return null;
  try {
    const v = sessionStorage.getItem(props.stateStorageKey);
    return v === 'expanded' || v === 'minimized' ? v : null;
  } catch {
    return null;
  }
}
function persistState(s: 'expanded' | 'minimized') {
  if (!props.stateStorageKey || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(props.stateStorageKey, s);
  } catch {
    /* quota / denied — non-fatal */
  }
}

function onState(state: 'expanded' | 'minimized') {
  expanded.value = state === 'expanded';
  persistState(state);
  if (expanded.value) scrollToBottom();
}

async function onNewSession() {
  // The person's own click ends first contact (clearSession flips it off). That
  // is not the host's late "suppress" decision, so the greeting watcher must not
  // read it as one and close the panel under her hand.
  autoOpened.value = false;
  await lumi.clearSession();
}

// FILES DROPPED INTO LUMI (ADR 0106). The whole panel is a drop target while a
// drag carrying files is over it; the paperclip opens the same picker. Each file
// uploads on its own the moment it is added, so the send only carries handles.
const fileInput = ref<HTMLInputElement | null>(null);
// The camera button (touch screens only, see its style): the same picker, opened
// straight on the back camera — the timesheet or the ID card in front of the
// person, without a detour through the photo library.
const cameraInput = ref<HTMLInputElement | null>(null);
const dragDepth = ref(0);
const dragging = computed(() => dragDepth.value > 0);
const uploading = computed(() => lumi.attachments.value.some((a) => a.status === 'uploading'));
const canSend = computed(
  () =>
    !lumi.sending.value &&
    !uploading.value &&
    (!!lumi.input.value.trim() || lumi.attachments.value.some((a) => a.status === 'ready')),
);

function carriesFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}
function onDragEnter(e: DragEvent) {
  if (!carriesFiles(e) || lumi.composerDisabled.value) return;
  dragDepth.value += 1;
}
function onDragLeave(e: DragEvent) {
  if (!carriesFiles(e)) return;
  dragDepth.value = Math.max(0, dragDepth.value - 1);
}
function onDrop(e: DragEvent) {
  dragDepth.value = 0;
  if (lumi.composerDisabled.value) return;
  const files = e.dataTransfer?.files;
  if (files?.length) void lumi.addFiles(files);
}
function onPick(e: Event) {
  const el = e.target as HTMLInputElement;
  if (el.files?.length) void lumi.addFiles(el.files);
  // The same file picked twice in a row still fires `change`.
  el.value = '';
}
// A screenshot or a copied file pasted into the composer (issue 1100) is
// attached like a picked one. Text pastes on as the textarea always did; so
// does copied rich text (Word, a web page), which can carry a picture of
// itself beside its text — the text is what the person meant.
const PASTED_IMAGE_EXT: Record<string, string> = { 'image/png': 'png', 'image/jpeg': 'jpg' };
function namePasted(file: File, i: number): File {
  // A clipboard image's name is the browser's to choose, sometimes none at
  // all; the upload goes by the name's extension. `image.png` is what Chrome
  // and Firefox call one themselves.
  const ext = PASTED_IMAGE_EXT[file.type];
  if (!ext || /\.(png|jpe?g)$/i.test(file.name)) return file;
  return new File([file], `image${i ? `-${i + 1}` : ''}.${ext}`, { type: file.type });
}
function onPaste(e: ClipboardEvent) {
  const data = e.clipboardData;
  const files = Array.from(data?.files ?? []);
  if (!data || !files.length || lumi.composerDisabled.value) return;
  if (data.getData('text/plain').trim() && Array.from(data.types).includes('text/html')) return;
  e.preventDefault();
  void lumi.addFiles(files.map(namePasted));
}
function chipError(a: AssistantAttachmentDraft): string {
  switch (a.error) {
    case 'type':
      return L.value.attachErrorType;
    case 'size':
      return L.value.attachErrorSize;
    case 'tooMany':
      return L.value.attachErrorTooMany;
    case 'scan':
      return L.value.attachErrorScan;
    default:
      return L.value.attachErrorGeneric;
  }
}
function messageFiles(msg: AssistantMessageView): AssistantUploadView[] {
  const list = msg.attachmentsJson?.uploads;
  return Array.isArray(list) ? list.filter((u) => u && typeof u.fileName === 'string') : [];
}

async function onSend() {
  if (!canSend.value) return;
  // `send()` pushes the person's own message before its first await, so the
  // jump lands on it right away — not only once Lumi's whole turn is back.
  const sent = lumi.send();
  scrollToBottom();
  await sent;
}

// Anything that grows the thread: a message added or replaced, the last one
// growing, a run card appearing or changing state, the pending row (issue 679)
// appearing or giving way to the answer.
const threadSig = computed(() => {
  const msgs = lumi.messages.value;
  const last = msgs[msgs.length - 1];
  const runs = lumi.runs.value.map((r) => `${r.id}:${r.status}`).join(',');
  return `${msgs.length}|${last?.id ?? ''}|${last?.content.length ?? 0}|${runs}|${lumi.sending.value ? 'pending' : ''}`;
});
const assistantCount = computed(() => lumi.messages.value.filter((m) => m.role === 'assistant').length);
watch(threadSig, () => {
  if (pinned.value) scrollToBottom();
});
watch(assistantCount, (count, before) => {
  if (count > before && !pinned.value) newBelow.value = true;
});

// WHERE A TURN'S RUNS ARE DRAWN (issue 534). `assistant-api` hands back
// `turnId` on both messages and runs; a run's card sits under the assistant
// message that shares its `turnId`, in creation order. `runs` arrives
// newest-first (§4 of the contract) — reversed once here rather than at every
// call site.
//
// A run with no answered turn of its own sits where it happened (issue 1074):
// under the last message written before it. That is every run an external
// agent parks here over MCP (ADR 0102) — its turn lives in the agent's own
// thread, so it never carries one of this thread's — and a run whose turn
// record was never written. Grouped at the end instead, they stayed pinned
// under whatever was said last, however much later that was. Only a run with
// nothing written after it still trails: the turn that is streaming right now
// (it moves under its answer once that is read back), or one without a time.
const runsAscending = computed(() => [...lumi.runs.value].reverse());
const runPlacement = computed(() => {
  const msgs = lumi.messages.value;
  const answerOfTurn = new Map<string, string>();
  for (const m of msgs) {
    if (m.role === 'assistant' && m.turnId) answerOfTurn.set(m.turnId, m.id);
  }
  const underMessage = new Map<string, AssistantRunView[]>();
  const trailing: AssistantRunView[] = [];
  for (const run of runsAscending.value) {
    const anchor = (run.turnId && answerOfTurn.get(run.turnId)) || anchorByTime(msgs, run);
    if (!anchor) {
      trailing.push(run);
      continue;
    }
    const group = underMessage.get(anchor);
    if (group) group.push(run);
    else underMessage.set(anchor, [run]);
  }
  return { underMessage, trailing };
});
/** The message a run follows in time — none when nothing was written after it. */
function anchorByTime(msgs: AssistantMessageView[], run: AssistantRunView): string | null {
  const at = run.createdAt ? Date.parse(run.createdAt) : Number.NaN;
  if (Number.isNaN(at)) return null;
  // A local echo (`local-N`) has no time yet; it is the newest line, so it is
  // never the one written after an older run.
  const next = msgs.findIndex((m) => !!m.createdAt && Date.parse(m.createdAt) > at);
  return next > 0 ? msgs[next - 1].id : null;
}
function runsForMessage(msg: AssistantMessageView): AssistantRunView[] {
  return runPlacement.value.underMessage.get(msg.id) ?? [];
}
const trailingRuns = computed(() => runPlacement.value.trailing);

// "Alle bestätigen" (issue 534, optional): one click through the same
// `approveRun` per card, offered only once a group holds more than one
// pending approval — a single card already has its own Approve button.
function pendingIds(group: AssistantRunView[]): string[] {
  return group.filter((r) => r.status === 'needs_approval').map((r) => r.id);
}
async function approveAll(group: AssistantRunView[]) {
  await Promise.all(pendingIds(group).map((id) => lumi.approveRun(id)));
}

function onFeedbackTextInput(messageId: string, event: Event) {
  lumi.setFeedbackText(messageId, (event.target as HTMLTextAreaElement).value);
}

// The `lumi:message` window-event bridge (in-page integrations open Lumi and
// send). Kept per-instance; multi-tab BroadcastChannel is a later phase.
let bridge: ((e: Event) => void) | null = null;

// Lumi's opening line (#419, #690). It is said ONCE in a person's life with Lumi:
// on their first contact, the one call that minted their thread — a fact the
// server owns (`firstContact`), so it survives reloads and new devices. An empty
// thread later on (a cleared session, a quiet day) is just an empty thread: it
// does not get a hello. (#678 briefly greeted every empty thread; that read as
// Lumi forgetting it had already said hello.)
//
// Two halves decide it: the host decides WHETHER (an empty greeting suppresses it
// — a workspace in demo mode), the server decides WHEN. It also waits for the
// thread to be read (`ready`), so a failed read never greets: an unread thread
// says nothing about what was said in it. Once shown it stays for this mount: the
// person's own first message must not pull the hello out from above it.
const greetingEligible = computed(() => lumi.ready.value && lumi.firstContact.value);
const greetingLatched = ref(false);
watch(
  greetingEligible,
  (eligible) => {
    if (eligible) greetingLatched.value = true;
  },
  { immediate: true },
);
const showGreeting = computed(() => greetingLatched.value && !!L.value.greeting);

// Any other empty thread gets one short line instead (issue 718): the long
// introduction is for the first contact only. Same wait for a successful read,
// so a failed read never claims the thread is empty.
const showEmptyHint = computed(
  () => !showGreeting.value && lumi.ready.value && !lumi.messages.value.length && !!L.value.emptyThread,
);

// ...and the panel opens itself to say it, but only to a person meeting Lumi for
// the first time (the server's `created`). A greeting nobody sees is not a
// greeting; this is the one thing in the product allowed to open this panel
// unasked, and it happens once in a person's life with it.
//
// The suppress decision can arrive AFTER the open (a host's onboarding status
// loads a beat behind the dock: demo mode is only known then). When it does and
// nothing has been said, the panel closes again — an unasked-for panel that
// opened onto an empty thread is the one state this must never leave behind.
//
// It opens in the DOCK (issue 993), not the default float: floating, it lay over
// the page and caught the person's first click. A mode the person has chosen
// (stored per tenant and user) still wins.
const autoOpened = ref(false);
const greetOnFirstContact = computed(() => lumi.firstContact.value && !!L.value.greeting);
watch(greetOnFirstContact, (greet) => {
  if (greet) {
    autoOpened.value = true;
    nextTick(() => shellRef.value?.expandPreferring('dock'));
  } else if (autoOpened.value) {
    autoOpened.value = false;
    // A host that wants the panel open anyway (demo mode) keeps it open.
    if (!lumi.messages.value.length && !props.openOnArrival) nextTick(() => shellRef.value?.minimize());
  }
});

// Open on arrival (demo mode): the host's decision can arrive after mount, like
// the greeting's suppression, so it is watched rather than read once. Only a
// session with no stored state opens — a minimize this session is a choice.
const mounted = ref(false);
watch(
  () => [mounted.value, props.openOnArrival, props.stateStorageKey] as const,
  ([isMounted, open]) => {
    if (!isMounted || !open || readStoredState() !== null) return;
    nextTick(() => shellRef.value?.expandPreferring('dock'));
  },
);

onMounted(() => {
  // Rehydrate expanded/minimized within this page's load (continuity).
  if (readStoredState() === 'expanded') {
    nextTick(() => shellRef.value?.expand());
  }
  bridge = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    const text = typeof detail === 'string' ? detail : '';
    if (!text) return;
    shellRef.value?.expand();
    const sent = lumi.sendExternal(text);
    scrollToBottom();
    void sent.then(scrollToBottom);
  };
  window.addEventListener('lumi:message', bridge as EventListener);
  mounted.value = true;
});
onBeforeUnmount(() => {
  lumiDockLauncher.working = false;
  if (bridge) window.removeEventListener('lumi:message', bridge as EventListener);
  bridge = null;
});
</script>

<template>
  <LumiShell
    ref="shellRef"
    :module="module"
    :title="L.title"
    :storage-key="modeStorageKey"
    :launcher-label="L.launcherLabel"
    :new-session-label="L.newSessionLabel"
    :minimize-label="L.minimizeLabel"
    :dock-label="L.dockLabel"
    :float-label="L.floatLabel"
    :resize-label="L.resizeLabel"
    @update:state="onState"
    @new-session="onNewSession"
  >
    <template #body>
      <div
        ref="messagesEl"
        class="lumi-assistant__body"
        @scroll.passive="onBodyScroll"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
      >

        <!-- Only an EMPTY thread gives way to the loading line: the re-read after
             a send must not blank the conversation, which would also throw away
             the reader's scroll position (issue 558). -->
        <div v-if="lumi.loading.value && !lumi.messages.value.length" class="lumi-assistant__hint">{{ L.loading }}</div>
        <div v-else class="lumi-assistant__stream">
          <!-- Lumi's opening line (#419). It sits at the top of the stream, ahead
               of anything the person goes on to say, because that is where it was
               said. It is not a message: it is never stored, so it is gone on the
               next page load — which is right for a greeting. -->
          <div v-if="showGreeting" class="lumi-assistant__msg lumi-assistant__msg--ai" data-testid="lumi-greeting">
            <div class="lumi-assistant__who">{{ L.ai }}</div>
            <div class="lumi-assistant__lines">
              <div class="lumi-assistant__line">{{ L.greeting }}</div>
            </div>
          </div>
          <div v-if="showEmptyHint" class="lumi-assistant__msg lumi-assistant__msg--ai" data-testid="lumi-empty-hint">
            <div class="lumi-assistant__who">{{ L.ai }}</div>
            <div class="lumi-assistant__lines">
              <div class="lumi-assistant__line">{{ L.emptyThread }}</div>
            </div>
          </div>
          <div
            v-for="msg in lumi.messages.value"
            :key="msg.id"
            class="lumi-assistant__msg"
            :class="msg.role === 'user' ? 'lumi-assistant__msg--user' : 'lumi-assistant__msg--ai'"
          >
            <div class="lumi-assistant__who">{{ msg.role === 'user' ? L.you : L.ai }}</div>
            <div class="lumi-assistant__lines">
              <!-- Markdown for Lumi's own answers (issue 534: paragraphs, bold/
                   italic/code, lists, links, the `[[…]]` entity-link token,
                   pipe tables). A person's own message stays plain text —
                   nothing they type is ever interpreted as markup. -->
              <LumiMarkdown
                v-if="msg.role === 'assistant'"
                :content="msg.content"
                :resolve-link="transport.resolveLink"
                :navigate="transport.navigate"
              />
              <div v-else-if="msg.content" class="lumi-assistant__line">{{ msg.content }}</div>
              <ul v-if="msg.role === 'user' && messageFiles(msg).length" class="lumi-assistant__files">
                <li v-for="file in messageFiles(msg)" :key="file.id" class="lumi-assistant__file">
                  <LumiIcon name="file" width="14" height="14" stroke-width="1.8" />
                  <span class="lumi-assistant__file-name">{{ file.fileName }}</span>
                </li>
              </ul>
            </div>

            <!--
              The person's own verdict, on every answer Lumi gives (issue 296,
              ADR 0071 D5, PDR 0023 rule 4). It is the one signal that catches
              "no error, but not what I wanted" — the run says `succeeded`, the
              turn says `answered`, and nothing deterministic can see that the
              answer was plausible and wrong.

              It sits on ASSISTANT MESSAGES, which is also how "on every
              approval-card outcome" is satisfied: an approval that commits, is
              refused by the module or fails writes its outcome into the thread
              as an assistant bubble, so the thumb is already on the sentence
              that says what happened. A second affordance on the run card would
              be a second write surface for the same fact.
            -->
            <div v-if="msg.role === 'assistant' && !String(msg.id).startsWith('local-')" class="lumi-assistant__feedback">
              <template v-if="lumi.feedback.value[msg.id]?.done">
                <span class="lumi-assistant__feedback-thanks">{{ L.feedbackThanks }}</span>
              </template>
              <template v-else-if="lumi.feedback.value[msg.id]?.picking">
                <div class="lumi-assistant__feedback-picker">
                  <div class="lumi-assistant__feedback-title">{{ L.feedbackReasonTitle }}</div>
                  <div class="lumi-assistant__feedback-reasons">
                    <button
                      v-for="reason in FEEDBACK_REASONS"
                      :key="reason.code"
                      type="button"
                      class="lumi-assistant__feedback-reason"
                      @click="lumi.submitFeedbackReason(msg.id, reason.code)"
                    >{{ L[reason.label] }}</button>
                  </div>
                  <textarea
                    class="lumi-assistant__feedback-textarea"
                    rows="2"
                    :placeholder="L.feedbackTextPlaceholder"
                    :value="lumi.feedbackText.value[msg.id] || ''"
                    @input="onFeedbackTextInput(msg.id, $event)"
                  />
                  <button
                    type="button"
                    class="lumi-assistant__feedback-dismiss"
                    @click="lumi.dismissFeedback(msg.id)"
                  >{{ L.feedbackDismiss }}</button>
                </div>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="lumi-assistant__feedback-btn"
                  :class="{ 'lumi-assistant__feedback-btn--on': lumi.feedback.value[msg.id]?.value === 'up' }"
                  :disabled="lumi.feedback.value[msg.id]?.saving"
                  :aria-label="L.feedbackUp"
                  :title="L.feedbackUp"
                  @click="lumi.rateMessage(msg.id, 'up')"
                >
                  <LumiIcon name="helpful" width="16" height="16" stroke-width="1.8" />
                </button>
                <button
                  type="button"
                  class="lumi-assistant__feedback-btn"
                  :class="{ 'lumi-assistant__feedback-btn--on': lumi.feedback.value[msg.id]?.value === 'down' }"
                  :disabled="lumi.feedback.value[msg.id]?.saving"
                  :aria-label="L.feedbackDown"
                  :title="L.feedbackDown"
                  @click="lumi.rateMessage(msg.id, 'down')"
                >
                  <LumiIcon name="not-helpful" width="16" height="16" stroke-width="1.8" />
                </button>
              </template>
              <!-- Reported in place, on the message it belongs to — the inline
                   tier, never a toast (ADR 0068 rule 2). -->
              <span
                v-if="lumi.feedback.value[msg.id]?.failed"
                class="lumi-assistant__feedback-error"
                role="alert"
              >{{ L.feedbackFailed }}</span>
            </div>

            <!-- The turn's own action cards (issue 534): drawn under the
                 assistant message of the turn that produced them, in creation
                 order, rather than one cumulative list after the stream. A card
                 without an answered turn sits under the message it followed in
                 time (issue 1074) — which may be the person's own. -->
            <div v-if="runsForMessage(msg).length" class="lumi-assistant__runs">
              <LumiRunCard
                v-for="run in runsForMessage(msg)"
                :key="run.id"
                :run="run"
                :labels="L"
                :draft="lumi.drafts.value[run.id]"
                :edit="lumi.edits.value[run.id]"
                :approve-state="lumi.approveState.value[run.id]"
                :resolve-link="transport.resolveLink"
                :navigate="transport.navigate"
                @edit-draft-body="lumi.editDraftBody(run.id, $event)"
                @approve="lumi.approveRun(run.id)"
                @cancel="lumi.cancelRun(run.id)"
              />
              <button
                v-if="pendingIds(runsForMessage(msg)).length > 1"
                type="button"
                class="lumi-assistant__btn lumi-assistant__btn--ghost lumi-assistant__runs-approve-all"
                @click="approveAll(runsForMessage(msg))"
              >{{ L.runApproveAll }}</button>
            </div>
          </div>
        </div>

        <!-- Runs with nothing written after them — the turn is still streaming,
             or the run carries no time. They move under their message the
             moment it exists (see `runPlacement`). -->
        <div v-if="trailingRuns.length" class="lumi-assistant__runs lumi-assistant__runs--trailing">
          <LumiRunCard
            v-for="run in trailingRuns"
            :key="run.id"
            :run="run"
            :labels="L"
            :draft="lumi.drafts.value[run.id]"
            :edit="lumi.edits.value[run.id]"
            :approve-state="lumi.approveState.value[run.id]"
            :resolve-link="transport.resolveLink"
            :navigate="transport.navigate"
            @edit-draft-body="lumi.editDraftBody(run.id, $event)"
            @approve="lumi.approveRun(run.id)"
            @cancel="lumi.cancelRun(run.id)"
          />
          <button
            v-if="pendingIds(trailingRuns).length > 1"
            type="button"
            class="lumi-assistant__btn lumi-assistant__btn--ghost lumi-assistant__runs-approve-all"
            @click="approveAll(trailingRuns)"
          >{{ L.runApproveAll }}</button>
        </div>

        <!-- Lumi is working on the turn the person just sent (issue 679). The
             blocking chat call can run for a while; this is the one visible sign
             that the question arrived. It sits last in the thread, after the
             person's own message and any run cards already streaming, drawn as
             Lumi's turn-in-waiting, and is gone the moment the answer is read
             back. `role="status"` + the `sending` label come from the spinner. -->
        <div v-if="lumi.sending.value" class="lumi-assistant__pending" data-testid="lumi-pending">
          <div class="lumi-assistant__who">{{ L.ai }}</div>
          <Spinner size="sm" :label="L.sending" />
        </div>

        <!-- Lumi answered below where the person is reading (issue 558). A
             zero-height sticky anchor at the end of the scroller, so the pill
             floats over the thread's bottom edge without taking up a line. -->
        <div v-if="newBelow" class="lumi-assistant__jump-anchor">
          <button type="button" class="lumi-assistant__jump" data-testid="lumi-new-message" @click="scrollToBottom">
            <LumiIcon name="jump-down" width="14" height="14" stroke-width="2" />
            {{ L.newMessage }}
          </button>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="lumi-assistant__composer">
        <span v-if="lumi.statusMessage.value" class="lumi-assistant__status">{{ lumi.statusMessage.value }}</span>
        <div v-if="lumi.composerDisabled.value" class="lumi-assistant__notice">{{ lumi.disabledNotice.value }}</div>
        <div
          v-else
          class="lumi-assistant__composer-card"
          :class="{ 'lumi-assistant__composer-card--drop': dragging }"
          @dragenter.prevent="onDragEnter"
          @dragover.prevent
          @dragleave="onDragLeave"
          @drop.prevent="onDrop"
        >
          <!-- Where the files land (ADR 0106): the whole panel takes the drop, the
               composer says so while files are dragged over any part of it. -->
          <div v-if="dragging" class="lumi-assistant__dropzone" data-testid="lumi-dropzone">
            <LumiIcon name="upload" width="18" height="18" stroke-width="1.8" />
            <span>{{ L.dropHere }}</span>
          </div>
          <ul v-if="lumi.attachments.value.length" class="lumi-assistant__chips" data-testid="lumi-attachments">
            <li
              v-for="a in lumi.attachments.value"
              :key="a.localId"
              class="lumi-assistant__chip"
              :class="{ 'lumi-assistant__chip--error': a.status === 'error' }"
            >
              <Spinner v-if="a.status === 'uploading'" size="sm" :label="L.attachUploading" />
              <LumiIcon v-else name="file" width="14" height="14" stroke-width="1.8" />
              <span class="lumi-assistant__chip-name" :title="a.fileName">{{ a.fileName }}</span>
              <span v-if="a.status === 'error'" class="lumi-assistant__chip-error" role="alert">{{ chipError(a) }}</span>
              <button
                type="button"
                class="lumi-assistant__chip-remove"
                :aria-label="`${L.attachRemove}: ${a.fileName}`"
                :title="L.attachRemove"
                @click="lumi.removeAttachment(a.localId)"
              >
                <LumiIcon name="remove" width="10" height="10" stroke-width="3" />
              </button>
            </li>
          </ul>
          <textarea
            v-model="lumi.input.value"
            rows="3"
            class="lumi-assistant__input"
            :placeholder="L.inputPlaceholder"
            @keydown.enter.exact.prevent="onSend"
            @paste="onPaste"
          />
          <div class="lumi-assistant__composer-row">
            <input
              ref="fileInput"
              type="file"
              multiple
              class="lumi-assistant__file-input"
              :accept="ASSISTANT_UPLOAD_ACCEPT"
              tabindex="-1"
              aria-hidden="true"
              data-testid="lumi-file-input"
              @change="onPick"
            />
            <button
              type="button"
              class="lumi-assistant__attach"
              :aria-label="L.attach"
              :title="L.attach"
              data-testid="lumi-attach"
              @click="fileInput?.click()"
            >
              <LumiIcon name="attachment" width="16" height="16" stroke-width="1.8" />
            </button>
            <input
              ref="cameraInput"
              type="file"
              accept="image/jpeg,image/png"
              capture="environment"
              class="lumi-assistant__file-input"
              tabindex="-1"
              aria-hidden="true"
              data-testid="lumi-camera-input"
              @change="onPick"
            />
            <button
              type="button"
              class="lumi-assistant__attach lumi-assistant__camera"
              :aria-label="L.takePhoto"
              :title="L.takePhoto"
              data-testid="lumi-camera"
              @click="cameraInput?.click()"
            >
              <LumiIcon name="camera" width="16" height="16" stroke-width="1.8" />
            </button>
            <button
              type="button"
              class="lumi-assistant__send"
              :disabled="!canSend"
              :aria-label="L.send"
              :title="lumi.sending.value ? L.sending : L.send"
              @click="onSend"
            >
              <LumiIcon name="send" width="16" height="16" stroke-width="1.8" />
            </button>
          </div>
        </div>
      </div>
    </template>
  </LumiShell>
</template>

<style>
/* The thread runs on the panel colour; head and composer sit in front of it
   (LumiShell's fades). Your own turns are wash bubbles on the right, Lumi's
   answers plain text on the panel — no bubble, no line (spec §6). */
/* This element is the scroller (scrollToBottom targets it), inside the
   shell body whose head and foot fade over its two ends. */
/* `overflow-x: hidden` (issue 534): the panel itself never scrolls sideways —
   a wide markdown table scrolls inside its own wrapper instead (see
   `.lumi-assistant__lines`'s `min-width: 0` and LumiMarkdown's table wrap). */
.lumi-assistant__body { height: 100%; overflow-y: auto; overflow-x: hidden; padding: var(--space-2) var(--space-4) var(--space-3); font-family: var(--font-body); }
.lumi-assistant__jump-anchor { position: sticky; bottom: 0; height: 0; display: flex; justify-content: center; }
.lumi-assistant__jump {
  position: absolute;
  bottom: var(--space-2);
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  border: none;
  border-radius: var(--radius-pill);
  background: var(--btn-primary-bg);
  color: var(--btn-primary-fg);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  white-space: nowrap;
  box-shadow: var(--shadow-md);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-standard);
}
.lumi-assistant__jump:hover { background: var(--accent-hover); }
.lumi-assistant__hint { font-size: var(--text-sm); color: var(--text-secondary); }
.lumi-assistant__stream { display: flex; flex-direction: column; gap: var(--space-4); }

.lumi-assistant__msg {
  display: flex;
  flex-direction: column;
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--text-body);
}
.lumi-assistant__msg--user { align-items: flex-end; }
/* Lumi's pending turn: the same who-line as an answer, the three dots where the
   words will be. Separated from what is above it like a thread entry is. */
.lumi-assistant__pending { display: flex; flex-direction: column; margin-top: var(--space-4); font-size: var(--text-sm); }
.lumi-assistant__msg--user .lumi-assistant__lines {
  max-width: 88%;
  background: var(--surface-wash);
  border-radius: var(--radius-lg);
  padding: var(--space-2) var(--space-3);
  color: var(--text-heading);
}
.lumi-assistant__who {
  font-size: var(--text-caps);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--text-tertiary);
  margin-bottom: var(--space-1);
}
/* `min-width: 0` (issue 534): a flex child otherwise refuses to shrink below
   its content's natural width, and a wide markdown table inside would push
   this bubble — and the panel around it — wider instead of scrolling in its
   own box (`.lumi-markdown__table-wrap`, LumiMarkdown.vue). */
.lumi-assistant__lines { display: flex; flex-direction: column; gap: var(--space-1); min-width: 0; }
/* The person's own words keep their line breaks, and a word longer than the
   bubble — a pasted path, a URL, an address — breaks inside itself: unbroken,
   it widened the whole thread past the panel and cut off its left edge. */
.lumi-assistant__line { white-space: pre-wrap; overflow-wrap: anywhere; }

.lumi-assistant__feedback { display: flex; align-items: center; gap: var(--space-1); margin-top: var(--space-2); flex-wrap: wrap; }
.lumi-assistant__feedback-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  padding: 0;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: color var(--dur-fast) var(--ease-standard), background var(--dur-fast) var(--ease-standard);
}
.lumi-assistant__feedback-btn:hover { color: var(--text-heading); background: var(--surface-wash); }
.lumi-assistant__feedback-btn--on { color: var(--btn-primary-fg); background: var(--btn-primary-bg); }
.lumi-assistant__feedback-btn--on:hover { color: var(--btn-primary-fg); background: var(--accent-hover); }
.lumi-assistant__feedback-btn:disabled { cursor: progress; opacity: 0.6; }
.lumi-assistant__feedback-thanks { font-size: var(--text-xs); color: var(--text-secondary); }
.lumi-assistant__feedback-error { font-size: var(--text-xs); color: var(--danger-text); }
.lumi-assistant__feedback-picker { width: 100%; display: flex; flex-direction: column; gap: var(--space-2); }
.lumi-assistant__feedback-title { font-size: var(--text-sm); font-weight: var(--weight-semibold); color: var(--text-heading); }
.lumi-assistant__feedback-reasons { display: flex; flex-wrap: wrap; gap: var(--space-1); }
.lumi-assistant__feedback-reason {
  height: var(--control-h-sm);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--surface-card);
  color: var(--text-body);
  font-family: inherit;
  font-size: var(--text-xs);
  padding: 0 var(--space-3);
  cursor: pointer;
}
.lumi-assistant__feedback-reason:hover { background: var(--surface-wash); color: var(--text-heading); }
.lumi-assistant__feedback-textarea {
  width: 100%;
  font-size: var(--text-sm);
  font-family: inherit;
  color: var(--text-heading);
  border: 1px solid var(--field-border);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
  background: var(--field-bg);
  resize: vertical;
}
.lumi-assistant__feedback-dismiss {
  align-self: flex-start;
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: var(--text-xs);
  color: var(--text-secondary);
  cursor: pointer;
  text-decoration: underline;
}

/* Actions: one card per run — white on the panel, so the approval reads as
   the thing to act on. Drawn inside the turn that produced it (issue 534),
   so the gap above is the message's own rhythm, not a section break. */
.lumi-assistant__runs {
  margin-top: var(--space-2);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
/* The trailing group (a still-streaming turn) sits after the whole stream. */
.lumi-assistant__runs--trailing { margin-top: var(--space-5); }
/* Placed in time under the person's own line (issue 1074): full width like
   every other card, not pushed to the right with their bubble. */
.lumi-assistant__msg--user .lumi-assistant__runs { align-self: stretch; }
.lumi-assistant__runs-approve-all { align-self: flex-start; }
.lumi-assistant__runs-title {
  font-size: var(--text-caps);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.lumi-assistant__run {
  font-size: var(--text-sm);
  padding: var(--space-3) var(--space-3) var(--space-3);
  background: var(--surface-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
}
.lumi-assistant__run-head { display: flex; align-items: center; justify-content: flex-end; gap: var(--space-2); margin-bottom: var(--space-2); }
.lumi-assistant__run-name { display: block; font-weight: var(--weight-semibold); color: var(--text-heading); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lumi-assistant__run-name--full { overflow: visible; white-space: normal; overflow-wrap: anywhere; }
.lumi-assistant__run-status { font-size: var(--text-xs); color: var(--text-secondary); flex-shrink: 0; }
.lumi-assistant__run-status--approval {
  padding: 3px 10px;
  border-radius: var(--radius-pill);
  background: var(--warning-soft);
  color: var(--warning-text);
  font-weight: var(--weight-medium);
}
/* The expand/collapse chevron (issue 534) — rotates open, never resizes the row. */
.lumi-assistant__run-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
}
.lumi-assistant__run-toggle:hover { color: var(--text-heading); background: var(--surface-wash); }
.lumi-assistant__run-toggle svg { transition: transform var(--dur-fast) var(--ease-standard); }
.lumi-assistant__run-toggle--open svg { transform: rotate(180deg); }
.lumi-assistant__run-detail { margin-top: var(--space-2); display: flex; flex-direction: column; gap: var(--space-2); }
.lumi-assistant__run-summary { margin: 0; color: var(--text-body); overflow-wrap: anywhere; white-space: pre-line; }
.lumi-assistant__run-lines { margin: 0; padding: 0 0 0 var(--space-3); color: var(--text-body); font-size: var(--text-xs); display: flex; flex-direction: column; gap: 2px; overflow-wrap: anywhere; }
.lumi-assistant__run-error { margin: 0; color: var(--danger-text); }
.lumi-assistant__run-open {
  align-self: flex-start;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-weight: var(--weight-medium);
  color: var(--text-heading);
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: var(--attention);
  text-decoration-thickness: 1.5px;
  text-underline-offset: 3px;
}
.lumi-assistant__run-open:hover { color: var(--attention-text); }
.lumi-assistant__run-actions { margin-top: var(--space-3); display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }
.lumi-assistant__draft-state { width: 100%; color: var(--text-secondary); }
.lumi-assistant__draft-state--error { color: var(--danger-text); }
.lumi-assistant__draft-fields { width: 100%; display: flex; flex-direction: column; gap: var(--space-2); }
/* Label above its value, like a section heading: the panel is too narrow for
   a label column (#1064). The value breaks inside a word, so an e-mail address
   or a file name wraps instead of running out of the card. */
.lumi-assistant__draft-field { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.lumi-assistant__draft-field > span:last-child { min-width: 0; overflow-wrap: anywhere; white-space: pre-line; }
.lumi-assistant__draft-field-label { font-size: var(--text-xs); color: var(--text-secondary); overflow-wrap: anywhere; hyphens: auto; }
.lumi-assistant__draft-dirty { font-weight: var(--weight-regular); color: var(--text-secondary); }
.lumi-assistant__draft-textarea {
  width: 100%;
  font-size: var(--text-sm);
  font-family: inherit;
  line-height: 1.5;
  color: var(--text-heading);
  border: 1px solid var(--field-border);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
  background: var(--field-bg);
  resize: vertical;
}
.lumi-assistant__btn {
  height: var(--control-h);
  border: none;
  border-radius: var(--radius-md);
  padding: 0 var(--space-4);
  font-family: inherit;
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  background: var(--btn-primary-bg);
  color: var(--btn-primary-fg);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-standard);
}
.lumi-assistant__btn:hover:not(:disabled) { background: var(--accent-hover); }
.lumi-assistant__btn:disabled { opacity: 0.5; cursor: not-allowed; }
.lumi-assistant__btn--ghost { background: none; color: var(--text-heading); }
.lumi-assistant__btn--ghost:hover:not(:disabled) { background: var(--surface-wash); }

/* The composer: a floating card over the thread (spec §6) — field surface,
   panel edge, the deep shadow; the send button sits inside it. The bottom
   inset is deeper than the sides so the shadow (12px offset) has room before
   the panel's overflow clips it at the rounded edge. */
.lumi-assistant__composer { padding: 0 var(--space-3) var(--space-4); display: flex; flex-direction: column; gap: var(--space-2); }
.lumi-assistant__notice {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  text-align: center;
  padding: var(--space-3);
  background: var(--surface-card);
  border: 1px solid var(--panel-edge);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}
.lumi-assistant__composer-card {
  position: relative;
  background: var(--field-bg);
  border: 1px solid var(--panel-edge);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  transition: box-shadow var(--dur-fast) var(--ease-standard);
}
.lumi-assistant__composer-card:focus-within { box-shadow: var(--shadow-lg), 0 0 0 2px var(--focus-ring); }
.lumi-assistant__input {
  display: block;
  width: 100%;
  font-size: var(--text-sm);
  font-family: inherit;
  line-height: 1.5;
  color: var(--text-heading);
  border: none;
  border-radius: var(--radius-lg);
  padding: var(--space-3) var(--space-3) var(--space-2);
  background: none;
  resize: none;
}
.lumi-assistant__input::placeholder { color: var(--text-tertiary); }
.lumi-assistant__input:focus { outline: none; }
.lumi-assistant__composer-row { display: flex; align-items: center; justify-content: flex-end; gap: var(--space-2); padding: 0 var(--space-2) var(--space-2); }
/* Files dropped into Lumi (ADR 0106). */
.lumi-assistant__composer-card--drop { box-shadow: var(--shadow-lg), 0 0 0 2px var(--focus-ring); }
.lumi-assistant__dropzone {
  position: absolute;
  inset: 0;
  z-index: var(--layer-raised);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border: 2px dashed var(--focus-ring);
  border-radius: var(--radius-lg);
  background: var(--field-bg);
  color: var(--text-heading);
  font-size: var(--text-sm);
  pointer-events: none;
}
.lumi-assistant__chips { display: flex; flex-wrap: wrap; gap: var(--space-1); margin: 0; padding: var(--space-2) var(--space-2) 0; list-style: none; }
.lumi-assistant__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  max-width: 100%;
  padding: 2px var(--space-1) 2px var(--space-2);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-card);
  color: var(--text-heading);
  font-size: var(--text-xs);
}
.lumi-assistant__chip--error { border-color: var(--danger-text); }
.lumi-assistant__chip-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 180px; }
.lumi-assistant__chip-error { color: var(--danger-text); }
.lumi-assistant__chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--text-secondary);
  cursor: pointer;
}
.lumi-assistant__chip-remove:hover { background: var(--surface-wash); color: var(--text-heading); }
.lumi-assistant__file-input { display: none; }
.lumi-assistant__attach {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  margin-right: auto;
  border: none;
  border-radius: var(--radius-md);
  background: none;
  color: var(--text-secondary);
  cursor: pointer;
}
.lumi-assistant__attach:hover { background: var(--surface-wash); color: var(--text-heading); }
/* The camera button only where there is a camera to point: a desktop browser
   ignores `capture` and would open the same file dialog as the paperclip. */
.lumi-assistant__camera { display: none; }
@media (pointer: coarse) {
  .lumi-assistant__composer-row .lumi-assistant__attach { margin-right: 0; }
  .lumi-assistant__composer-row .lumi-assistant__camera { display: inline-flex; margin-right: auto; }
  .lumi-assistant__attach,
  .lumi-assistant__send { width: 40px; height: 40px; }
}
.lumi-assistant__files { display: flex; flex-direction: column; gap: 2px; margin: var(--space-1) 0 0; padding: 0; list-style: none; }
.lumi-assistant__file { display: inline-flex; align-items: center; gap: var(--space-1); font-size: var(--text-xs); color: var(--text-secondary); }
.lumi-assistant__file-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lumi-assistant__status { text-align: center; font-size: var(--text-xs); color: var(--text-secondary); }
.lumi-assistant__send {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  border: none;
  border-radius: var(--radius-md);
  background: var(--btn-primary-bg);
  color: var(--btn-primary-fg);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-standard);
}
.lumi-assistant__send:hover:not(:disabled) { background: var(--accent-hover); }
.lumi-assistant__send:disabled { cursor: not-allowed; opacity: 0.5; }
</style>
